import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/store/auth'

interface School {
  id: string
  name: string
}

interface Props {
  show: boolean
  schools: School[]
  submitting?: boolean
  error?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateUserModal',
  props: {
    show: { type: Boolean },
    schools: {},
    submitting: { type: Boolean, default: false },
    error: { default: '' }
  },
  emits: ['close', 'submit'],
  setup(__props: any, { emit: __emit }) {

/* eslint-disable no-undef */
const authStore = useAuthStore()

const props = __props

const emit = __emit

const formRef = ref<any>(null)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const formData = ref({
  email: '',
  first_name: '',
  last_name: '',
  organization: '',
  school: null as string | null,
  role: 'user',
  password: '',
  confirmPassword: ''
})

const showModal = computed({
  get: () => props.show,
  set: (value) => {
    if (!value) emit('close')
  }
})

const schoolOptions = computed(() => {
  return props.schools.map(school => ({
    title: school.name,
    value: school.id
  }))
})

// Get available roles from auth store with disabled state for roles above current user
const roleOptions = computed(() => {
  // Use the perceived role (authUser.role, which is overridden during "view as")
  const currentRole = authStore.authUser.role
  const currentUserLevel = authStore.roleHierarchy[currentRole] || 0

  // Get all roles from hierarchy
  const allRoles = Object.keys(authStore.roleHierarchy)
    .filter(role => role !== 'pending' && role !== 'root') // Exclude pending and root from options
    .map(role => {
      const roleLevel = authStore.roleHierarchy[role] || 0
      return {
        title: authStore.roleDisplayNames[role] || role,
        value: role,
        disabled: roleLevel > currentUserLevel // Disable roles above current user's level
      }
    })
    .sort((a, b) => {
      const levelA = authStore.roleHierarchy[a.value] || 0
      const levelB = authStore.roleHierarchy[b.value] || 0
      return levelB - levelA // Sort by level descending
    })

  return allRoles
})

const closeModal = () => {
  formData.value = {
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    school: null,
    role: 'user',
    password: '',
    confirmPassword: ''
  }
  showPassword.value = false
  showConfirmPassword.value = false
  emit('close')
}

const submitUser = async () => {
  const { valid } = await formRef.value.validate()

  if (!valid) {
    return
  }

  const userData = {
    email: formData.value.email,
    first_name: formData.value.first_name,
    last_name: formData.value.last_name,
    organization: formData.value.organization,
    school: formData.value.school,
    role: formData.value.role,
    password: formData.value.password
  }

  emit('submit', userData)
}

// Reset form when modal closes
watch(() => props.show, (newValue) => {
  if (!newValue) {
    formRef.value?.reset()
  }
})

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showModal).value = $event)),
    "max-width": "600px",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, null, {
            default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
              _createElementVNode("span", { class: "text-h5" }, "Create New User", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_form, {
                ref_key: "formRef",
                ref: formRef
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: formData.value.email,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.email) = $event)),
                    label: "Email",
                    type: "email",
                    variant: "outlined",
                    rules: [
              v => !!v || 'Email is required',
              v => /.+@.+\..+/.test(v) || 'Email must be valid'
            ],
                    required: "",
                    class: "mb-2"
                  }, null, 8, ["modelValue", "rules"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: formData.value.first_name,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.first_name) = $event)),
                    label: "First Name",
                    variant: "outlined",
                    class: "mb-2"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: formData.value.last_name,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.last_name) = $event)),
                    label: "Last Name",
                    variant: "outlined",
                    class: "mb-2"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_select, {
                    modelValue: formData.value.school,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.school) = $event)),
                    items: schoolOptions.value,
                    label: "School",
                    variant: "outlined",
                    clearable: "",
                    class: "mb-2"
                  }, null, 8, ["modelValue", "items"]),
                  _createVNode(_component_v_select, {
                    modelValue: formData.value.role,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.role) = $event)),
                    items: roleOptions.value,
                    label: "Role",
                    variant: "outlined",
                    rules: [v => !!v || 'Role is required'],
                    required: "",
                    class: "mb-2"
                  }, null, 8, ["modelValue", "items", "rules"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: formData.value.password,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.password) = $event)),
                    label: "Password",
                    type: showPassword.value ? 'text' : 'password',
                    variant: "outlined",
                    rules: [
              v => !!v || 'Password is required',
              v => v.length >= 8 || 'Password must be at least 8 characters'
            ],
                    required: "",
                    "append-inner-icon": showPassword.value ? 'mdi-eye-off' : 'mdi-eye',
                    "onClick:appendInner": _cache[6] || (_cache[6] = ($event: any) => (showPassword.value = !showPassword.value)),
                    class: "mb-2"
                  }, null, 8, ["modelValue", "type", "rules", "append-inner-icon"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: formData.value.confirmPassword,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.value.confirmPassword) = $event)),
                    label: "Confirm Password",
                    type: showConfirmPassword.value ? 'text' : 'password',
                    variant: "outlined",
                    rules: [
              v => !!v || 'Please confirm password',
              v => v === formData.value.password || 'Passwords must match'
            ],
                    required: "",
                    "append-inner-icon": showConfirmPassword.value ? 'mdi-eye-off' : 'mdi-eye',
                    "onClick:appendInner": _cache[8] || (_cache[8] = ($event: any) => (showConfirmPassword.value = !showConfirmPassword.value))
                  }, null, 8, ["modelValue", "type", "rules", "append-inner-icon"]),
                  (__props.error)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 0,
                        type: "error",
                        variant: "tonal",
                        class: "mt-4"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(__props.error), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 512)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                color: "grey",
                onClick: closeModal,
                disabled: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_component_v_btn, {
                color: "primary",
                onClick: submitUser,
                loading: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                  _createTextVNode(" Create User ", -1)
                ]))]),
                _: 1
              }, 8, ["loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})